import { apiClient } from "@/shared/apiClient";
import { authResponseSchema, authInitializeRequestSchema, authLoginRequestSchema } from "@/shared/zod";
import type { AuthInitializeRequest, AuthLoginRequest, AuthResponse } from "./types";
import { notificationService } from "@/lib/notifications";
import { logger } from "@/shared/logger";

/**
 * Ensures FCM token is available for initialization (requests permissions)
 */
async function ensureFcmTokenForInitialize(): Promise<string | null> {
  const maxRetries = 3;
  const retryDelay = 1000; // 1 second
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      logger.info("auth.fcm.initialize.attempt", { attempt, maxRetries });
      
      // Try to initialize and get token (with permission request)
      let fcmToken = await notificationService.initialize(true);
      
      // If not available, try to get from storage
      if (!fcmToken) {
        fcmToken = notificationService.getToken();
      }
      
      // If we have a token, return it
      if (fcmToken) {
        logger.info('auth.fcm.initialize.success');
        return fcmToken;
      }
      
      // If this is not the last attempt, wait before retrying
      if (attempt < maxRetries) {
        logger.warn("auth.fcm.initialize.retry", { retryDelay, attempt });
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }
    } catch (error) {
      logger.error(`auth.fcm.initialize.failed`, { attempt, error });
      
      // If this is not the last attempt, wait before retrying
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }
    }
  }
  
  logger.warn('auth.fcm.initialize.unavailableAfterRetries');
  return null;
}

/**
 * Ensures FCM token is available for login. Uses stored/memory token if present;
 * if permission already granted, initializes without prompting; otherwise requests
 * permission so we can send fcm_token with the login request.
 */
async function ensureFcmTokenForLogin(): Promise<string | null> {
  try {
    logger.info('auth.fcm.login.attempt');
    
    // First check if we have a stored token and permission is granted
    const hasStoredToken = await notificationService.hasValidFcmTokenInStorage();
    const storedPermission = await notificationService.getStoredPermissionStatus();
    
    if (hasStoredToken && storedPermission === 'granted') {
      logger.info('auth.fcm.login.fromStorage');
      const token = notificationService.getToken();
      return token;
    }
    
    // Try to get existing token from memory (fastest option)
    let fcmToken = notificationService.getToken();
    if (fcmToken) {
      logger.info('auth.fcm.login.fromMemory');
      return fcmToken;
    }
    
    // If permission already granted but no token, get token without prompting
    if (storedPermission === 'granted') {
      logger.info('auth.fcm.login.quickInit');
      fcmToken = await notificationService.initialize(false);
      if (fcmToken) {
        logger.info('auth.fcm.login.quickInitSuccess');
        return fcmToken;
      }
    }
    
    // No token yet: request permission and initialize so we can send fcm_token with login
    logger.info('auth.fcm.login.requestPermission');
    fcmToken = await notificationService.initialize(true);
    if (fcmToken) {
      logger.info('auth.fcm.login.permissionSuccess');
      return fcmToken;
    }
    
    logger.warn('auth.fcm.login.unavailable');
    return null;
  } catch (error) {
    logger.error('auth.fcm.login.failed', { error });
    return null;
  }
}

export const authApi = {
  async initialize(data: AuthInitializeRequest): Promise<AuthResponse> {
    // Validate request data
    authInitializeRequestSchema.parse(data);
    
    // Get notification data - ensure proper initialization with retry mechanism (requests permissions)
    const fcmToken = await ensureFcmTokenForInitialize();
    const deviceId = await notificationService.getDeviceId();
    const platform = notificationService.getPlatform();
    
    // Log initialize request payload for debugging device / FCM behavior
    logger.info('auth.initialize.request', {
      whatsapp_number: data.whatsapp_number,
      unique_identifier: data.unique_identifier,
      device_id: data.device_id || deviceId,
      platform: data.platform || platform,
      fcm_token: data.fcm_token || fcmToken,
    });
    
    // Call external API directly
    const response = await apiClient.post("/api/auth/initialize", {
      whatsapp_number: data.whatsapp_number,
      unique_identifier: data.unique_identifier,
      pin_code: data.pin_code,
      device_id: data.device_id || deviceId,
      platform: data.platform || platform,
      fcm_token: data.fcm_token || fcmToken,
    });
    
    // Log initialize raw response (without dumping full token contents)
    logger.info('[authApi.initialize] Raw response:', {
      has_access_token: !!response.access_token || !!response.token,
      employee_id: response.employee?.id,
      device_trusted: response.device_trusted,
      message: response.message,
    });
    
    // Transform external API response to match our expected format
    const transformedResponse = {
      access_token: response.access_token || response.token,
      expires_in: response.expires_in || 43200,
      employee: response.employee || {
        id: response.employee?.id || 1,
        whatsapp_number: data.whatsapp_number,
        unique_identifier: data.unique_identifier,
        has_pin: true,
      },
    };
    
    return apiClient.validateResponse(transformedResponse, authResponseSchema);
  },

  async login(data: AuthLoginRequest): Promise<AuthResponse> {
    // Validate request data
    authLoginRequestSchema.parse(data);
    
    // Get notification data - try to get FCM token quickly, don't block login
    let fcmToken = await ensureFcmTokenForLogin();
    const deviceId = await notificationService.getDeviceId();
    const platform = notificationService.getPlatform();
    
    // If no FCM token available, initialize notifications in background after login
    if (!fcmToken) {
      logger.warn('auth.login.fcmUnavailableWillProceed');
    }
    
    // Log login request payload for debugging device / FCM behavior
    logger.info('[authApi.login] Request payload:', {
      whatsapp_number: data.whatsapp_number,
      device_id: data.device_id || deviceId,
      platform: data.platform || platform,
      fcm_token: data.fcm_token || fcmToken,
    });
    
    // Call v2 auth endpoint
    const response = await apiClient.post("/api/auth/v2/login", {
      whatsapp_number: data.whatsapp_number,
      pin_code: data.pin_code,
      device_id: data.device_id || deviceId,
      platform: data.platform || platform,
      fcm_token: data.fcm_token || fcmToken,
    });
    
    // Log login raw response (without dumping full token contents)
    logger.info('[authApi.login] Raw response:', {
      has_access_token: !!response.access_token || !!response.token,
      employee_id: response.employee?.id,
      device_trusted: response.device_trusted,
      message: response.message,
    });
    
    // Transform external API response to match our expected format
    const transformedResponse = {
      access_token: response.access_token || response.token,
      expires_in: response.expires_in || 43200,
      employee: response.employee || {
        id: response.employee?.id || 1,
        whatsapp_number: data.whatsapp_number,
        unique_identifier: response.employee?.unique_identifier,
        has_pin: true,
      },
      device_trusted: response.device_trusted ?? true,
      message: response.message,
    };
    
    return apiClient.validateResponse(transformedResponse, authResponseSchema);
  },
};
